import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { type Header, flexRender } from '@tanstack/react-table'
import { useRef, useState } from 'react'
import type { ColumnMeta } from './TableView'
import { TruncatedCell } from './TruncatedCell'
import { getColumnHeaderTooltipTestId } from './testIds'

const ALIGNMENT_CLASS = { left: 'text-left', center: 'text-center', right: 'text-right' }

interface ColumnHeaderLabelProps<TData> {
	header: Header<TData, unknown>
	align: 'left' | 'center' | 'right'
	dataTestId?: string
}

export function ColumnHeaderLabel<TData>({ header, align, dataTestId }: ColumnHeaderLabelProps<TData>) {
	const labelRef = useRef<HTMLDivElement>(null)
	const [open, setOpen] = useState(false)
	const [isCutOff, setIsCutOff] = useState(false)

	const label = flexRender(header.column.columnDef.header, header.getContext())
	const headerTooltip = (header.column.columnDef.meta as ColumnMeta | undefined)?.headerTooltip

	if (!headerTooltip) {
		return (
			<TruncatedCell className='w-full font-medium' align={align}>
				{label}
			</TruncatedCell>
		)
	}

	const tooltipTestId = getColumnHeaderTooltipTestId(dataTestId, header.column.id)

	// Controlled, so the measurement lands in the same render that opens the tooltip.
	const handleOpenChange = (next: boolean) => {
		if (next && labelRef.current) {
			setIsCutOff(labelRef.current.scrollWidth > labelRef.current.clientWidth)
		}
		setOpen(next)
	}

	return (
		<Tooltip open={open} onOpenChange={handleOpenChange}>
			<TooltipTrigger asChild>
				<span data-testid={tooltipTestId} className='w-full min-w-0 flex cursor-pointer'>
					<div
						ref={labelRef}
						data-testid={tooltipTestId && `${tooltipTestId}-label`}
						className={cn('block w-full min-w-0 truncate font-medium', ALIGNMENT_CLASS[align])}>
						{label}
					</div>
				</span>
			</TooltipTrigger>
			<TooltipContent side='top' sideOffset={4} className={cn('max-w-100', isCutOff && 'p-0')}>
				{isCutOff ? (
					<>
						<div
							data-testid={tooltipTestId && `${tooltipTestId}-title`}
							className='border-b border-border px-3 py-2 font-medium break-words'>
							{label}
						</div>
						<div className='px-3 py-2'>{headerTooltip}</div>
					</>
				) : (
					headerTooltip
				)}
			</TooltipContent>
		</Tooltip>
	)
}
